import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { LINKS } from '../constants/links';
import { apiUrl } from '../utils/apiConfig';
import { getToken } from '../utils/auth';
import { showToast } from '../utils/toast';
import { openExternalUrl } from '../utils/openExternalUrl';
import './styles/VkLinkPage.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${PUBLIC_URL}/logo.png`;

const VkLogo = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.19 1.367 1.26 2.182 1.818.616.422 1.084.33 1.084.33l2.177-.03s1.138-.071.598-.964c-.044-.073-.314-.661-1.618-1.869-1.365-1.263-1.182-1.058.462-3.241.999-1.328 1.399-2.14 1.273-2.486-.12-.33-.859-.243-.859-.243l-2.451.015s-.182-.025-.316.056c-.13.079-.214.263-.214.263s-.385 1.023-.897 1.893c-1.08 1.834-1.513 1.931-1.69 1.818-.412-.264-.309-1.06-.309-1.626 0-1.768.268-2.503-.522-2.695-.263-.064-.456-.106-1.127-.113-.861-.009-1.59.003-2.003.206-.275.135-.487.435-.358.452.16.021.522.098.714.38.247.364.238 1.182.238 1.182s.142 2.25-.33 2.529c-.324.191-.769-.199-1.725-1.845-.489-.84-.859-1.77-.859-1.77s-.071-.174-.198-.267c-.154-.113-.37-.149-.37-.149l-2.328.015s-.35.01-.478.162c-.114.135-.009.414-.009.414s1.805 4.219 3.847 6.342c1.872 1.947 3.995 1.819 3.995 1.819h.963z" />
  </svg>
);

const VkLinkPage = () => {
  const navigate = useNavigate();
  const { user, onUserUpdate, openExternal } = useOutletContext() || {};
  const [linked, setLinked] = useState(Boolean(user?.vk_linked));
  const [vkUserId, setVkUserId] = useState(user?.vk_user_id || null);
  const [code, setCode] = useState('');
  const [display, setDisplay] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLinked(Boolean(user?.vk_linked));
  }, [user?.vk_linked]);

  useEffect(() => {
    if (!expiresAt) {
      setLeft(0);
      return undefined;
    }
    const tick = () => setLeft(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  useEffect(() => {
    const token = getToken();
    if (!token || token === 'undefined') return undefined;
    let cancelled = false;
    axios
      .get(apiUrl('/api/profile/vk'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      })
      .then(({ data }) => {
        if (cancelled || !data) return;
        setLinked(Boolean(data.linked));
        setVkUserId(data.vk_user_id || null);
        if (onUserUpdate && user) {
          onUserUpdate({ ...user, vk_linked: Boolean(data.linked) });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const authHeaders = () => {
    const token = getToken();
    if (!token || token === 'undefined') return null;
    return { Authorization: `Bearer ${token}` };
  };

  const openBot = () => {
    const url = LINKS.vkCommunityDm;
    if (typeof openExternal === 'function') openExternal(url, 'бота расписания ВКонтакте');
    else openExternalUrl(url);
  };

  const generate = async () => {
    const headers = authHeaders();
    if (!headers) {
      showToast('Войдите снова', 'warn', 'Нужна авторизация');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        apiUrl('/api/profile/vk'),
        { op: 'generate' },
        { headers, timeout: 12000 }
      );
      if (!data?.ok) throw new Error(data?.error || 'fail');
      setCode(data.code || '');
      setDisplay(data.code_display || data.code || '');
      setExpiresAt(Date.now() + (Number(data.ttl_seconds) || 300) * 1000);
      showToast('Код создан', 'ok', 'Отправьте код боту ВК');
    } catch (err) {
      showToast('Ошибка', 'warn', err?.response?.data?.error || 'Не удалось создать код');
    } finally {
      setBusy(false);
    }
  };

  const copy = useCallback(async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      showToast('Скопировано', 'ok', display || code);
    } catch {
      showToast('Скопируйте вручную', 'warn', display || code);
    }
  }, [code, display]);

  const unlink = async () => {
    const headers = authHeaders();
    if (!headers) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        apiUrl('/api/profile/vk'),
        { op: 'unlink' },
        { headers, timeout: 12000 }
      );
      setLinked(false);
      setVkUserId(null);
      setCode('');
      setDisplay('');
      setExpiresAt(0);
      if (onUserUpdate && user) onUserUpdate({ ...user, vk_linked: false });
      showToast('Отвязано', 'ok', data?.unlinked ? 'ВК отвязан' : 'Привязки не было');
    } catch {
      showToast('Ошибка', 'warn', 'Не удалось отвязать');
    } finally {
      setBusy(false);
    }
  };

  const ttlLabel = left
    ? `осталось ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`
    : code
      ? 'истёк — сгенерируйте новый'
      : 'Нажмите «Сгенерировать»';

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/profile');
  };

  return (
    <div className="vk-page">
      <header className="vk-page__top">
        <button type="button" className="vk-page__back" onClick={goBack} aria-label="Назад">
          <i className="bi bi-chevron-left" aria-hidden="true" />
        </button>
      </header>

      <div className="vk-page__layout">
        <div className="vk-page__visual" aria-hidden="true">
          <img className="vk-page__logo-kkt" src={LOGO_SRC} alt="" width={112} height={112} decoding="async" />
          <span className="vk-page__plus">+</span>
          <span className="vk-page__logo-vk">
            <VkLogo />
          </span>
        </div>

        <div className="vk-page__main">
          {linked ? (
            <>
              <h1 className="vk-page__title">ВКонтакте привязан</h1>
              <p className="vk-page__lead">
                {vkUserId ? <>Аккаунт связан с ВК id {vkUserId}. </> : null}
                Бот открывает расписание и ссылку для сброса пароля.
              </p>
              <div className="vk-page__actions">
                <div className="vk-page__form-row">
                  <button type="button" className="vk-page__btn vk-page__btn--danger" disabled={busy} onClick={unlink}>
                    Отвязать
                  </button>
                  <button type="button" className="vk-page__btn vk-page__btn--bot" onClick={openBot}>
                    Открыть бота ВК
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="vk-page__title">
                Создайте <span className="vk-page__title-code">код</span>
                <br />
                для ВКонтакте
              </h1>
              <p className="vk-page__lead">
                Сгенерируйте код, скопируйте и отправьте боту сообщества. Код действует{' '}
                <strong>5 минут</strong> — после привязки откроются расписание и восстановление пароля.
              </p>

              <div className={`vk-page__token${code ? '' : ' is-empty'}`}>
                <span className="vk-page__token-label">
                  <i className="bi bi-key" aria-hidden="true" />
                  Ваш код
                </span>
                <strong className="vk-page__token-value">{display || '—— ——'}</strong>
                <span className="vk-page__token-ttl">{ttlLabel}</span>
              </div>

              <div className="vk-page__actions">
                <div className="vk-page__form-row">
                  <button
                    type="button"
                    className="vk-page__btn vk-page__btn--primary"
                    disabled={busy}
                    onClick={generate}
                  >
                    {busy ? '…' : 'Сгенерировать'}
                  </button>
                  <button
                    type="button"
                    className="vk-page__btn vk-page__btn--copy"
                    disabled={!code || busy}
                    onClick={copy}
                  >
                    Скопировать
                  </button>
                </div>
                <button type="button" className="vk-page__btn vk-page__btn--bot" onClick={openBot}>
                  Открыть бота ВК
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VkLinkPage;
